import { flatten, get } from 'lodash';
import InputType from 'models/enums/InputType';
import ProductDocumentPropertyPath from 'models/enums/ProductDocumentPropertyPath';
import ProductRefundDocumentPropertyPath from 'models/enums/ProductRefundDocumentPropertyPath';
import { convertDocumentPropertyByInputType } from './Utils.Other.ProductDocumentInputTypeMappings';

export function removeSignatureFields<T extends { key: string }>(fields: T[], signatureFields: { key: string }[]): T[] {
  return fields.filter((field) => !signatureFields?.some((signatureField) => signatureField.key === field.key));
}

export function getProductDocumentsFinalFields(
  contract: Model.IContract,
  productDocuments: Model.IProductDocument[],
  additionalFields: {
    productDocumentId: number;
    key: string;
    value: string | number | boolean;
  }[]
) {
  const fillablePdfFields = removeSignatureFields(
    flatten(productDocuments?.map((d) => d.fillablePdfFields ?? [])),
    flatten(productDocuments?.map((d) => d.signatureFields ?? []))
  );

  return fillablePdfFields.map((f) => {
    if (f.propertyPath) {
      return {
        productDocumentId: f.productDocumentId,
        key: f.key,
        value: convertDocumentPropertyByInputType({
          propertyPath: f.propertyPath as ProductDocumentPropertyPath,
          documentCategory: 'ProductDocument',
          value: get({ contract: contract }, f.propertyPath),
          inputLabel: f.label || f.key,
          inputType: f.type as InputType,
          options: {
            isPackage: contract?.isPackage,
            isTaxExempt: contract?.client?.taxExempt,
          },
        }),
      };
    } else {
      const additionalField = additionalFields?.find(
        (it) => it.key == f.key && it.productDocumentId === f.productDocumentId
      );
      return {
        productDocumentId: f.productDocumentId,
        key: f.key,
        value: convertDocumentPropertyByInputType({
          value: additionalField?.value,
          documentCategory: 'ProductDocument',
          inputLabel: f.label || f.key,
          inputType: f.type as InputType,
          options: {
            isPackage: contract?.isPackage,
            isTaxExempt: contract?.client?.taxExempt,
          },
        }),
      };
    }
  });
}

export function getProductEnhancementsDocumentsFinalFields(
  contract: Model.IContract,
  productEnhancementDocuments: Model.IProductEnhancementDocument[],
  enhancementAdditionalFields: {
    productEnhancementDocumentId: number;
    key: string;
    value: string | number | boolean;
  }[]
) {
  const fillablePdfFields = removeSignatureFields(
    flatten(productEnhancementDocuments?.map((d) => d.productEnhancementAdditionalFields)),
    []
  );

  return fillablePdfFields.map((f) => {
    if (f.propertyPath) {
      return {
        productEnhancementDocumentId: f.productEnhancementDocumentId,
        key: f.key,
        value: convertDocumentPropertyByInputType({
          propertyPath: f.propertyPath as ProductDocumentPropertyPath,
          documentCategory: 'ProductEnhancementDocument',
          value: get({ contract: contract }, f.propertyPath),
          inputLabel: f.label || f.key,
          inputType: f.type as InputType,
          options: {
            isPackage: contract?.isPackage,
            isTaxExempt: contract?.client?.taxExempt,
            revision: {
              exist: contract?.revisionNumber > 0,
              number: contract?.revisionNumber,
            },
          },
        }),
      };
    } else {
      const enhancementAdditionalField = enhancementAdditionalFields?.find(
        (it) => it.key == f.key && it.productEnhancementDocumentId === f.productEnhancementDocumentId
      );
      return {
        productEnhancementDocumentId: f.productEnhancementDocumentId,
        key: f.key,
        value: convertDocumentPropertyByInputType({
          value: enhancementAdditionalField?.value,
          documentCategory: 'ProductEnhancementDocument',
          inputLabel: f.label || f.key,
          inputType: f.type as InputType,
          options: {
            isPackage: contract?.isPackage,
            isTaxExempt: contract?.client?.taxExempt,
            revision: {
              exist: contract?.revisionNumber > 0,
              number: contract?.revisionNumber,
            },
          },
        }),
      };
    }
  });
}

export function getProductRefundDocumentsFinalFields(
  data: { contract: Model.IContract; contractRefund: Model.IContractRefund },
  productRefundDocuments: Model.IProductRefundDocument[],
  additionalFields: {
    documentId: number;
    key: string;
    value: string | number | boolean;
  }[]
) {
  return flatten(productRefundDocuments?.map((d) => d.fillablePdfFields)).map((f) => {
    if (f.propertyPath) {
      return {
        documentId: f.productRefundDocumentId,
        key: f.key,
        value: convertDocumentPropertyByInputType({
          propertyPath: f.propertyPath as ProductRefundDocumentPropertyPath,
          documentCategory: 'ProductRefundDocument',
          value: get(data, f.propertyPath),
          inputLabel: f.label || f.key,
          inputType: f.type as InputType,
          options: {
            isPackage: data.contract?.isPackage,
            isTaxExempt: data.contract?.client?.taxExempt,
          },
        }),
      };
    } else {
      const additionalField = additionalFields?.find(
        (it) => it.key == f.key && it.documentId === f.productRefundDocumentId
      );
      return {
        documentId: f.productRefundDocumentId,
        key: f.key,
        value: convertDocumentPropertyByInputType({
          value: additionalField?.value,
          documentCategory: 'ProductRefundDocument',
          inputLabel: f.label || f.key,
          inputType: f.type as InputType,
          options: {
            isPackage: data.contract?.isPackage,
            isTaxExempt: data.contract?.client?.taxExempt,
          },
        }),
      };
    }
  });
}
